import BreadCrumb from "components/Common/BreadCrumb";
import TableContainer from "components/Common/TableContainer";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
} from "reactstrap";
import { createSelector } from "reselect";
import {
  NumberParam,
  useQueryParams,
  withDefault,
} from "use-query-params";

import CountUp from "react-countup";
import { formatDateStringToKorea } from "helpers/format";
import CopyWrapper from "components/Common/CopyWrapper";
import { getAIContentTBED } from "store/content/thunk";
import CollapseContent from "components/Common/CollapseContent";
import ModalDetail from "./ModalDetail";

registerLocale("en", en);
registerLocale("ko", ko);

const AIContentTBEDlList = () => {
  const { t, i18n } = useTranslation();
  const dispatch: any = useDispatch();
  const [detail, setDetail] = useState<any>(null)

  const [query, setQuery]: any = useQueryParams({
    time_request: withDefault(NumberParam, 0),
    page: withDefault(NumberParam, 1),
    limit: withDefault(NumberParam, 30),
  });

  const selectLayoutState = (state: any) => state.AIContentTBED;

  const ContentProperties = createSelector(selectLayoutState, (state) => ({
    contents: state.contents,
    isContentLoading: state.isContentLoading,
    isContentSuccess: state.isContentSuccess,

    error: state.error,
  }));

  // Inside your component
  const { contents, isContentLoading } = useSelector(
    ContentProperties
  );

  function loadTableData() {
    dispatch(getAIContentTBED(query));
  }

  useEffect(() => {
    loadTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, JSON.stringify(query)]);


  const handleShowDetail = (value: any) => () => {
    setDetail(value)
  }

  // Column
  const columns = useMemo(
    () => [
      {
        Header: t("No."),
        thWidth: 60,
        thClass: "whitespace-nowrap",
        Cell: ({ ...props }) => {
          return (
            <div style={{ width: 35 }}>
              {contents?.pagination?.total -
                ((query?.page - 1) * query?.limit + props?.row?.index)}
            </div>
          );
        },
      },
      {
        Header: t("Topic"),
        accessor: "topic",
        filterable: false,
        sortable: false,
        thWidth: 100,
        thClass: "text-center",
        Cell: (cell: any) => {
          return (
            <div
              className=""
              style={{ minWidth: "80px" }}
            >
              {cell?.value || ""}
            </div>
          );
        },
      },
      {
        Header: t("Title"),
        accessor: "title",
        filterable: false,
        sortable: false,
        thWidth: 140,
        thClass: "text-center whitespace-nowrap",
        Cell: (cell: any) => {
          return (
            <div className="text-center" style={{ minWidth: 80 }}>
              {cell?.value || ""}
            </div>
          );
        },
      },
      {
        Header: t("Type"),
        accessor: "type",
        filterable: false,
        sortable: false,
        thWidth: 140,
        thClass: "text-center whitespace-nowrap",
        Cell: (cell: any) => {
          return (
            <div className="text-center" style={{ minWidth: 80 }}>
              {cell?.value || ""}
            </div>
          );
        },
      },
      {
        Header: t("Summary"),
        accessor: "summary",
        filterable: true,
        sortable: false,
        thWidth: 240,
        thClass: "whitespace-nowrap",
        Cell: (cell: any) => {
          const title = cell?.value || "";

          return title ? (
            <CopyWrapper contentCopy={title} style={{ minWidth: "220px" }}>
              <CollapseContent
                style={{ maxWidth: "180px" }}
                dataOriginal={title}
                isRank={false}
                isInlineBlock={true}
                isShowModal={true}
                titleModal={t("Title")}
                numberCharactor={50}
              />
            </CopyWrapper>
          ) : "";
        },
      },
      {
        Header: t("Body"),
        accessor: "body",
        filterable: true,
        sortable: false,
        thWidth: 280,
        thClass: "whitespace-nowrap",
        Cell: (cell: any) => {
          const content = cell?.value || "";
          return content ? (
            <CopyWrapper contentCopy={content} style={{ minWidth: "260px" }}>
              <CollapseContent
                style={{ maxWidth: "350px" }}
                dataOriginal={content}
                isRank={false}
                isInlineBlock={true}
                isShowModal={true}
                titleModal={t("Content")}
              />
            </CopyWrapper>
          ) : "";
        },
      },
      {
        Header: t("Prompt"),
        accessor: "prompt.prompt",
        filterable: true,
        sortable: false,
        thWidth: 280,
        thClass: "whitespace-nowrap",
        Cell: (cell: any) => {
          return <div style={{ width: "280px" }} onClick={handleShowDetail(cell?.row?.original)}>
            <p className="line-clamp-2 cursor-pointer">{cell?.value || ""}</p>
          </div>;
        },
      },
      {
        Header: t("Response"),
        accessor: "prompt.response",
        filterable: true,
        sortable: false,
        thWidth: 280,
        thClass: "whitespace-nowrap",
        Cell: (cell: any) => {
          return <div style={{ width: "280px" }} onClick={handleShowDetail(cell?.row?.original)}>
            <p className="line-clamp-2 cursor-pointer">{cell?.value || ""}</p>
          </div>;
        },
      },
      {
        Header: t("Hashtags"),
        accessor: "hashtags",
        filterable: true,
        sortable: false,
        thWidth: 200,
        thClass: "whitespace-nowrap",
        Cell: (cell: any) => {
          return (
            cell?.value ? (
              <div className="d-flex gap-1 flex-wrap" style={{ width: "180px" }}>
                {
                  cell?.value?.map((item: any, index: any) => <div key={index} className="rounded-pill badge bg-primary">{item}</div>)
                }
              </div>
            ) : "-"
          );
        },
      },

      {
        Header: t("Created at"),
        accessor: "createdAt",
        filterable: true,
        sortable: false,
        thWidth: 130,
        thClass: "text-end whitespace-nowrap",
        Cell: (cell: any) => {
          const date = formatDateStringToKorea(cell?.value);
          const arrDate = String(date || "").split(" ");

          return (
            <div className="text-end" style={{ minWidth: "100px" }}>
              <span>
                <span>{arrDate?.[0] || ""}</span> <br />{" "}
                <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
              </span>
            </div>
          );
        },
      },
      {
        Header: t("Updated at"),
        accessor: "updatedAt",
        filterable: true,
        sortable: false,
        thWidth: 130,
        thClass: "text-end whitespace-nowrap",
        Cell: (cell: any) => {
          const date = formatDateStringToKorea(cell?.value);
          const arrDate = String(date || "").split(" ");

          return (
            <div className="text-end" style={{ minWidth: "100px" }}>
              <span>
                <span>{arrDate?.[0] || ""}</span> <br />{" "}
                <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
              </span>
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n?.language, contents?.pagination?.total, query?.page]
  );

  const handleChangePage = useCallback((page: any) => {
    setQuery({ page: page + 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeSorting = useCallback((sortBy: any) => {
    setQuery((_prev: any) => {
      return { ..._prev, ...sortBy };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  useEffect(() => {
    document.title = `${t("Community AI (TBED)")} | OnnyJob`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={t("Community AI (TBED)")} pageTitle={t("Home")} />
          <Row>
            <Col lg={12}>
              <Card
                id="customerList"
                style={{ boxShadow: "0px 3px 4px 0px rgba(0, 0, 0, 0.03)" }}
              >
                <CardHeader className="border-0">
                  <Row className="g-4 align-items-center">
                    <div className="col-5 con-sm-6">
                      <div>
                        <h5 className="card-title mb-0">
                          <span className="me-2">{t("Total")}:</span>
                          <CountUp
                            start={0}
                            end={contents?.pagination?.total || 0}
                            duration={1}
                            className="text-primary"
                          />
                        </h5>
                      </div>
                    </div>

                  </Row>
                </CardHeader>
                <CardBody className="pt-3">
                  <div>
                    <TableContainer
                      className="custom-header-css"
                      divClass="table-responsive table-card"
                      tableClass="align-middle table-bordered-dashed"
                      theadClass="table-light text-muted"
                      columns={columns}
                      data={
                        contents?.items?.length ? contents?.items : []
                      }
                      customPageSize={query.limit}
                      customPageIndex={query.page - 1}
                      totalRecords={contents?.pagination?.total}
                      customPageCount={Math.ceil(
                        Number(contents?.pagination?.total) /
                        Number(query.limit)
                      )}
                      handleChangePage={handleChangePage}
                      manualSorting={true}
                      sorting={{
                        sortBy: query.sortBy,
                        orderBy: query.orderBy,
                      }}
                      handleChangeSorting={handleChangeSorting}
                      isLoading={isContentLoading}
                    />
                  </div>
                  <ToastContainer closeButton={false} limit={1} />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        {!!detail && (
          <ModalDetail
            data={detail}
            onClose={() => setDetail(null)}
          />
        )}
      </div>
    </React.Fragment>
  );
};

export default AIContentTBEDlList;
